import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader, Btn, Badge, Card, Empty, Spinner, Input, Select, useToast } from '../components/shared/UI';
import { fmtCurrency, fmtDate, getDocType, getStatus, balanceDue, DOC_TYPES, STATUS_OPTIONS } from '../utils/helpers';
import { useT } from '../i18n';
import api from '../utils/api';

export default function DocumentList({ filterType }) {
  const navigate = useNavigate();
  const { t } = useT();
  const { show, ToastContainer } = useToast();
  const [docs, setDocs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('');
  const [deleting, setDeleting] = useState(null);

  const typeInfo = filterType ? getDocType(filterType) : null;
  const typeLabel = filterType ? t(`docType.${filterType}`) : t('docs.allDocuments');
  const title = filterType ? t(`docs.titlePlural.${filterType}`) : t('docs.allDocuments');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('type', filterType);
      if (status)     params.set('status', status);
      if (search)     params.set('search', search);
      const { data } = await api.get(`/documents?${params}`);
      setDocs(data);
    } finally { setLoading(false); }
  }, [filterType, status, search]);

  useEffect(() => { load(); }, [load]);

  const del = async id => {
    if (!window.confirm(t('docs.confirmDelete'))) return;
    setDeleting(id);
    await api.delete(`/documents/${id}`);
    setDocs(d => d.filter(x => x._id !== id));
    setDeleting(null);
  };

  const updateStatus = async (id, s) => {
    try {
      // The server may settle the balance (mark as paid) or refuse (fully paid by payments)
      const { data } = await api.patch(`/documents/${id}/status`, { status: s });
      setDocs(d => d.map(x => x._id === id ? { ...x, status: data.status, amountPaid: data.amountPaid } : x));
    } catch (err) { show(err.response?.data?.message || t('editor.errorSaving'), 'error'); }
  };

  const duplicate = async id => {
    try {
      const { data } = await api.post(`/documents/${id}/duplicate`);
      navigate(`/documents/${data._id}/edit`);
    } catch { window.alert(t('docs.duplicateFailed')); }
  };

  return (
    <div>
      <ToastContainer />
      <PageHeader
        title={title}
        subtitle={t('docs.count', { n: docs.length })}
        actions={
          <Btn variant="primary" onClick={() => navigate(`/documents/new${filterType ? `?type=${filterType}` : ''}`)}>
            + {t('docs.newType', { type: filterType ? typeLabel : t('docs.document') })}
          </Btn>
        }
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Input
          placeholder={t('docs.searchPlaceholder')}
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        {!filterType && (
          <Select value={filterType || ''} disabled style={{ width: 160 }}>
            <option value="">{t('docs.allTypes')}</option>
            {DOC_TYPES.map(x => <option key={x.value} value={x.value}>{t(`docType.${x.value}`)}</option>)}
          </Select>
        )}
        <Select value={status} onChange={e => setStatus(e.target.value)} style={{ width: 140 }}>
          <option value="">{t('docs.allStatus')}</option>
          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{t(`status.${s.value}`)}</option>)}
        </Select>
      </div>

      {loading ? <Spinner /> : docs.length === 0
        ? <Empty icon={typeInfo?.icon || '📁'} message={t('docs.noneFound', { title })}
            action={<Btn variant="primary" onClick={() => navigate(`/documents/new${filterType ? `?type=${filterType}` : ''}`)}>
              {t('docs.createFirst', { type: typeLabel })}
            </Btn>} />
        : (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                  {!filterType && <th style={th}>{t('docs.thType')}</th>}
                  <th style={th}>{t('docs.thNumber')}</th>
                  <th style={th}>{t('docs.thClient')}</th>
                  <th style={th}>{t('docs.thDate')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('docs.thAmount')}</th>
                  <th style={th}>{t('docs.thStatus')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('docs.thActions')}</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((doc, i) => {
                  const st = getStatus(doc.status);
                  const dt = getDocType(doc.type);
                  return (
                    <tr key={doc._id} style={{ borderBottom: '1px solid var(--border-light)', background: i % 2 === 0 ? 'var(--card-bg)' : 'var(--hover-bg)' }}>
                      {!filterType && (
                        <td style={td}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            {dt.icon} <span style={{ color: dt.color, fontWeight: 500 }}>{t(`docType.${doc.type}`)}</span>
                          </span>
                        </td>
                      )}
                      <td style={td}>
                        <button onClick={() => navigate(`/documents/${doc._id}`)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--blue)', fontWeight: 600, fontSize: 13, fontFamily: 'inherit', padding: 0 }}>
                          {doc.docNumber}
                        </button>
                      </td>
                      <td style={{ ...td, color: 'var(--text-muted)' }}>
                        {doc.clientSnapshot?.name || doc.clientSnapshot?.company || '—'}
                      </td>
                      <td style={{ ...td, color: 'var(--text-muted)', fontSize: 13 }}>{fmtDate(doc.issueDate)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>
                        {fmtCurrency(doc.total, doc.currencySymbol)}
                        {doc.amountPaid > 0 && balanceDue(doc) > 0 && (
                          <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>
                            {t('payments.balanceShort', { amount: fmtCurrency(balanceDue(doc), doc.currencySymbol) })}
                          </div>
                        )}
                      </td>
                      <td style={td}>
                        <select value={doc.status} onChange={e => updateStatus(doc._id, e.target.value)}
                          style={{
                            border: `1px solid ${st.color}40`, borderRadius: 20, padding: '3px 8px',
                            fontSize: 12, fontWeight: 600, color: st.color, background: st.color + '12',
                            cursor: 'pointer', outline: 'none', fontFamily: 'inherit'
                          }}>
                          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{t(`status.${s.value}`)}</option>)}
                        </select>
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <Btn size="sm" variant="ghost" onClick={() => navigate(`/documents/${doc._id}`)}>{t('docs.view')}</Btn>
                          <Btn size="sm" variant="ghost" onClick={() => navigate(`/documents/${doc._id}/edit`)}>{t('docs.edit')}</Btn>
                          <Btn size="sm" variant="ghost" onClick={() => duplicate(doc._id)}>{t('docs.copy')}</Btn>
                          <Btn size="sm" variant="danger" disabled={deleting === doc._id}
                            onClick={() => del(doc._id)}>{t('docs.del')}</Btn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </Card>
        )
      }
    </div>
  );
}

const th = {
  padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
  textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap'
};
const td = { padding: '13px 16px', fontSize: 14, color: 'var(--text)', verticalAlign: 'middle' };
